import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Zap, Crown, Wallet, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";
const BORDER = "#E7E5E2";

const fmtBRL = (v) => (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * <PaywallModal>
 * Aparece quando `useEntitlement().guard()` retorna needsPaywall.
 *
 * Ações:
 *   - Pagar via Carteira (POST /billing/usage/reserve com aceite de overage)
 *   - Ver Planos (upgrade recorrente)
 *   - Recarregar Carteira (abre WalletChip flow)
 *
 * Props:
 *   entitlement: retorno de /billing/entitlements/{code}
 *   quantity: unidades a consumir
 *   overageAmount: valor calculado do overage
 *   onConfirmPay: callback quando usuário aceita pagar via carteira
 *   onCancel: fechar
 */
export default function PaywallModal({
  entitlement, quantity = 1, overageAmount = 0,
  overage_units = 0, always_paid = false,
  onConfirmPay, onCancel,
}) {
  const [busy, setBusy] = useState(false);

  if (!entitlement) return null;

  const svcName = entitlement.service_name || entitlement.service_code;
  const included = entitlement.included || 0;
  const consumed = entitlement.consumed || 0;
  const remaining = entitlement.remaining ?? 0;

  const handlePayWallet = async () => {
    setBusy(true);
    try {
      // Reserva no backend (backend decide franquia vs overage)
      const r = await api.post("/billing/usage/reserve", {
        service_code: entitlement.service_code,
        quantity,
      });
      // Executa a callback do consumidor com o reservation_id
      await onConfirmPay?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao reservar consumo");
    } finally { setBusy(false); }
  };

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" style={{ zIndex: 2147483400 }}
        onClick={onCancel} data-testid="paywall-backdrop"/>
      <div className="fixed inset-0 flex items-center justify-center px-4" style={{ zIndex: 2147483401 }}>
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl" data-testid="paywall-modal">
          <div className="px-6 py-5 border-b flex items-start justify-between" style={{ borderColor: BORDER }}>
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
                {always_paid ? "Serviço avulso" : "Franquia mensal esgotada"}
              </div>
              <div className="font-serif text-2xl mt-1" style={{ color: NAVY }}>{svcName}</div>
            </div>
            <button onClick={onCancel} className="w-9 h-9 rounded-full hover:bg-slate-100 inline-flex items-center justify-center"
              data-testid="paywall-close"><X className="w-4 h-4 text-slate-500"/></button>
          </div>

          <div className="px-6 py-5">
            {!always_paid && (
              <div className="rounded-xl bg-slate-50 border p-4 mb-4" style={{ borderColor: BORDER }}>
                <div className="text-[11px] uppercase tracking-widest text-slate-500">Consumo do ciclo · plano {entitlement.plan_name}</div>
                <div className="mt-1 text-sm text-slate-700">
                  <span className="font-semibold tabular-nums">{consumed}</span> de{" "}
                  <span className="font-semibold tabular-nums">{included}</span> unidades usadas
                </div>
                <div className="mt-1 text-xs text-emerald-700">Restam {remaining} unidades gratuitas neste mês.</div>
              </div>
            )}

            <div className="rounded-xl border-2 p-4 mb-4"
              style={{ borderColor: GOLD, background: `${GOLD}0A` }}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: GOLD }}>
                  Pagar com créditos
                </span>
                <Wallet className="w-4 h-4" style={{ color: GOLD }}/>
              </div>
              <div className="text-sm text-slate-700">
                {always_paid ? (
                  <>Este serviço é <strong>avulso</strong>. Valor total: <strong className="tabular-nums">{fmtBRL(overageAmount)}</strong></>
                ) : (
                  <>Você usará <strong>{Math.min(quantity - overage_units, remaining)}</strong> unidade(s) da franquia + <strong>{overage_units}</strong> excedente por <strong className="tabular-nums">{fmtBRL(overageAmount)}</strong></>
                )}
              </div>
              <button onClick={handlePayWallet} disabled={busy}
                data-testid="paywall-pay-wallet"
                className="w-full mt-3 h-11 rounded-lg text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ background: NAVY }}>
                <Zap className="w-4 h-4"/> {busy ? "Reservando…" : `Confirmar e cobrar ${fmtBRL(overageAmount)}`}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Link to="/plans" onClick={onCancel} data-testid="paywall-upgrade"
                className="h-11 rounded-lg border text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-slate-50"
                style={{ borderColor: BORDER, color: NAVY }}>
                <Crown className="w-3.5 h-3.5" style={{ color: GOLD }}/> Ver planos <ChevronRight className="w-3 h-3"/>
              </Link>
              <button onClick={onCancel} data-testid="paywall-cancel"
                className="h-11 rounded-lg border text-[12px] text-slate-500 hover:bg-slate-50"
                style={{ borderColor: BORDER }}>
                Cancelar
              </button>
            </div>

            <div className="mt-4 text-[10.5px] text-slate-400 text-center">
              Preços validados pelo servidor · saldo debita apenas após confirmação
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
